import ActionStatus from '@sunbit-dev/common/enums/ActionStatus';
import ApplicationError from '@sunbit-dev/common/models/ApplicationError';
import IIdentificationRequest from '@sunbit-dev/common/models/cardApply/IIdentificationRequest';
import ILoginResponse from '@sunbit-dev/common/models/cardApply/ILoginResponse';
import CommonThunk from '../models/CommonThunk';
import sunbitApi from '../services/sunbitApi';
import ICreditLineDetails from '@sunbit-dev/common/models/cardApply/ICreditLineDetails';
import ApplicationStatus from '@sunbit-dev/common/enums/cardApply/ApplicationStatus';
import IApplicationDetails from '@sunbit-dev/common/models/cardApply/IApplicationDetails';
import IExistingPaymentMethod from '@sunbit-dev/common/models/cardApply/IExistingPaymentMethod';
import { showPlasticAddressModal } from '../actions/plasticAddressActions';
import { replace } from 'connected-react-router';
import URLS from '../constants/urls';
import { ThunkDispatch } from 'redux-thunk';
import { handleVerification } from './verificationActions';
import { AnyAction } from 'redux';
import { ActionTypes } from '../models/actions/identification';

const identifyPending = () => ({
  type: ActionTypes.Identify,
  status: ActionStatus.Pending,
});

const identifySuccess = (loginResponse?: ILoginResponse) => ({
  type: ActionTypes.Identify,
  status: ActionStatus.Success,
  ...loginResponse,
  error: undefined as any,
});

const identifyFailure = (error: ApplicationError) => ({
  type: ActionTypes.Identify,
  status: ActionStatus.Failure,
  error,
});

const applyForCreditLineAction = (creditLineDetails: ICreditLineDetails) => ({
  type: ActionTypes.ApplyForCreditLine,
  ...creditLineDetails,
});

const getApplicationDetailsAction = (applicationDetails: IApplicationDetails) => ({
  type: ActionTypes.getApplicationDetails,
  ...applicationDetails,
});

const getExistingPaymentMethodsAction = (existingPaymentMethods: IExistingPaymentMethod[]) => ({
  type: ActionTypes.getExistingPaymentMethods,
  existingPaymentMethods,
});

export const identifyThunk = (
  identificationRequest: IIdentificationRequest,
  refferingPartner: string | undefined
): CommonThunk => {
  return async (dispatch) => {
    dispatch(identifyPending());
    try {
      if (!refferingPartner) {
        delete identificationRequest.dateOfBirth;
      }

      const loginResponse = await sunbitApi.login(identificationRequest);
      sunbitApi.setApplicationToken(loginResponse.token);

      const results = await Promise.all([
        sunbitApi.getApplicationDetails(),
        sunbitApi.updateApplicationDetails(identificationRequest),
      ]);

      const applicationDetails = results[0];
      dispatch(getApplicationDetailsAction(applicationDetails));

      // sunbitApi.applyForCreditLine() is separated from the previous requests because some cases (like active consumer alert) where it's throwing an error and still we want to use previous requests data.
      const creditLineDetails = await sunbitApi.applyForCreditLine();
      const existingPaymentMethods = await sunbitApi.getExistingPaymentMethods()
      dispatch(applyForCreditLineAction(creditLineDetails));
      dispatch(getExistingPaymentMethodsAction(existingPaymentMethods));
      dispatch(identifySuccess(loginResponse));
      dispatch(showPlasticAddressModal());
    } catch (error: unknown) {
      dispatch(identifyFailure(error as ApplicationError));
    }
  };
};

export const existingApplicationThunk = (ssnLast4Digits: string): CommonThunk => {
  return async (dispatch) => {
    dispatch(identifyPending());
    try {
      const loginResponse = await sunbitApi.login({ ssnLast4Digits });
      sunbitApi.setApplicationToken(loginResponse.token);
      const applicationDetails = await sunbitApi.getApplicationDetails();

      if (
        applicationDetails &&
        [
          ApplicationStatus.CREDIT_LINE_DATA_PROVIDED,
          ApplicationStatus.CONSUMER_ALERT,
          ApplicationStatus.CREDIT_APPROVED,
        ].includes(applicationDetails.applicationStatus)
      ) {
        // in case of CONSUMER_ALERT or CREDIT_LINE_DATA_PROVIDED but still the application fails (due to risk or whatever) the call to sunbitApi.applyForCreditLine() will handle error message flow.
        const results = await Promise.all([sunbitApi.getExistingPaymentMethods(), sunbitApi.applyForCreditLine()]);

        const existingPaymentMethods = results[0];
        const creditLineDetails = results[1];

        dispatch(getExistingPaymentMethodsAction(existingPaymentMethods));
        dispatch(applyForCreditLineAction(creditLineDetails));
      }

      dispatch(getApplicationDetailsAction(applicationDetails));
      handleExistingApplicationAfterLogin(dispatch, applicationDetails.applicationStatus, loginResponse);
    } catch (error: unknown) {
      dispatch(identifyFailure(error as ApplicationError));
    }
  };
};

const handleExistingApplicationAfterLogin = (
  dispatch: ThunkDispatch<{}, {}, AnyAction>,
  status: ApplicationStatus,
  loginResponse: ILoginResponse
) => {
  switch (status) {
    case ApplicationStatus.COMPLETED:
      dispatch(replace(URLS.applicationThanks));
      dispatch(identifySuccess(loginResponse));
      break;
    case ApplicationStatus.VERIFICATION:
      dispatch(handleVerification());
      break;
    default:
      dispatch(identifySuccess(loginResponse));
      dispatch(showPlasticAddressModal());
  }
};

export const checkExistingPaymentMethodsAndCreditLine = (): CommonThunk => {
  return async (dispatch) => {
    dispatch(identifyPending());
    try {
      const results = await Promise.all([sunbitApi.getExistingPaymentMethods(), sunbitApi.applyForCreditLine()]);

      const existingPaymentMethods = results[0];
      const creditLineDetails = results[1];

      dispatch(getExistingPaymentMethodsAction(existingPaymentMethods));
      dispatch(applyForCreditLineAction(creditLineDetails));
      dispatch(identifySuccess());
      dispatch(showPlasticAddressModal());
    } catch (error: unknown) {
      dispatch(identifyFailure(error as ApplicationError));
    }
  };
};

export type Actions =
  | ReturnType<typeof identifyPending>
  | ReturnType<typeof identifySuccess>
  | ReturnType<typeof identifyFailure>;
